import React, { useState, useEffect, useRef } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Link } from 'react-router'
import { Users, Search, History } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface UserOverview {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  organisationName: string | null
  activeModules: string[]
  inactiveModules: string[]
  lastLoggedInAt: string | null
}

interface UserOverviewPage {
  users: UserOverview[]
  totalCount: number
}

type SubscriptionFilter = 'all' | 'active' | 'inactive'

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

const GET_ALL_USERS_OVERVIEW = gql`
  query getAllUsersOverview($searchName: String, $searchOrganisation: String, $subscriptionStatus: String, $limit: Int, $offset: Int) {
    getAllUsersOverview(searchName: $searchName, searchOrganisation: $searchOrganisation, subscriptionStatus: $subscriptionStatus, limit: $limit, offset: $offset) {
      users {
        id
        email
        firstName
        lastName
        organisationName
        activeModules
        inactiveModules
        lastLoggedInAt
      }
      totalCount
    }
  }
`

const displayName = (user: UserOverview) => (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email.split('@')[0])

const formatLastLogin = (value: string | null) => (value ? new Date(value).toLocaleString() : 'Never')

const FILTER_OPTIONS: { value: SubscriptionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

const UsersView: React.FC = () => {
  const [users, setUsers] = useState<UserOverview[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nameSearch, setNameSearch] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const [debouncedNameSearch, setDebouncedNameSearch] = useState('')
  const [debouncedOrgSearch, setDebouncedOrgSearch] = useState('')
  const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionFilter>('all')
  const sentinelRef = useRef<HTMLDivElement>(null)
  // Bumped on every fetch so a slow earlier response can't clobber newer results
  const requestSeqRef = useRef(0)
  const loadingMoreRef = useRef(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameSearch(nameSearch.trim())
      setDebouncedOrgSearch(orgSearch.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [nameSearch, orgSearch])

  const fetchPage = async (offset: number) => {
    const seq = ++requestSeqRef.current
    const append = offset > 0
    try {
      if (append) {
        loadingMoreRef.current = true
        setLoadingMore(true)
      } else {
        setLoading(true)
        setError(null)
      }
      const res = await authorizedGraphQLRequest<{ getAllUsersOverview: UserOverviewPage }>(GET_ALL_USERS_OVERVIEW, {
        searchName: debouncedNameSearch || null,
        searchOrganisation: debouncedOrgSearch || null,
        subscriptionStatus: subscriptionFilter,
        limit: PAGE_SIZE,
        offset,
      })
      if (seq !== requestSeqRef.current) return
      const page = res.getAllUsersOverview
      setTotalCount(page.totalCount)
      setUsers((prev) => (append ? [...prev, ...page.users] : page.users))
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return
      const message = err?.response?.errors?.[0]?.message || 'Failed to load users'
      setError(message)
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false)
        loadingMoreRef.current = false
        setLoadingMore(false)
      }
    }
  }

  // First page on mount and whenever the criteria change
  useEffect(() => {
    fetchPage(0)
  }, [debouncedNameSearch, debouncedOrgSearch, subscriptionFilter])

  const hasMore = users.length < totalCount

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || loading) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          fetchPage(users.length)
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loading, users.length, debouncedNameSearch, debouncedOrgSearch, subscriptionFilter])

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 mb-4">{error}</p>
        <Button variant="outline" onClick={() => fetchPage(0)}>
          Retry
        </Button>
      </div>
    )
  }

  const hasCriteria = debouncedNameSearch !== '' || debouncedOrgSearch !== '' || subscriptionFilter !== 'all'

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-xl font-semibold text-gray-800">Users</h3>
          <span className="text-sm text-gray-500">({totalCount})</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Filter by subscription status">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setSubscriptionFilter(option.value)}
                className={`px-3 py-2 text-sm transition-colors ${subscriptionFilter === option.value ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              placeholder="Search by name or email"
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              placeholder="Search by organisation"
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {users.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{hasCriteria ? 'No users match your search' : 'No users found'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-medium">Full name</th>
                <th className="px-4 py-3 font-medium">Organisation</th>
                <th className="px-4 py-3 font-medium">Active subscriptions</th>
                <th className="px-4 py-3 font-medium">Inactive subscriptions</th>
                <th className="px-4 py-3 font-medium">Last logged in</th>
                <th className="px-4 py-3 font-medium sr-only">Activity</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{displayName(user)}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{user.organisationName || '—'}</td>
                  <td className="px-4 py-3">
                    {user.activeModules.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.activeModules.map((name) => (
                          <span key={name} className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                            {name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {user.inactiveModules.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.inactiveModules.map((name) => (
                          <span key={name} className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                            {name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatLastLogin(user.lastLoggedInAt)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {/* Everything that happened to this account, newest first */}
                    <Link to={`/dashboard?view=activity&userId=${user.id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" aria-label={`Activity of ${user.email}`}>
                      <History className="h-3.5 w-3.5" />
                      Activity
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && (
            <div ref={sentinelRef} className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UsersView
